import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { DependencyList } from 'react';

type RATCustomObject = {
  [key: string]: string | number;
};

type DataLayerValues = {
  cpParams: RATCustomObject;
  cvParams: RATCustomObject;
};

type DialogRatEventType = 'click' | 'appear';

type UseRatDialogEventsParams = {
  baseCompId?: string;
};

/**
 * Parses the cp params of RAT extracted from the DOM's input element and returns a corresponding object.
 *
 * The function removes surrounding curly braces and splits key-value pairs contained in a string.
 * Keys and values are extracted by matching patterns, and numeric strings are converted to numbers when applicable.
 *
 * @param {string} value - The string value from the input element containing cp params.
 * @returns {RATCustomObject} The parsed key-value object.
 *
 * @example
 * // Example input:
 * const inputValue = "{'device_id':'YuA6.lAAGZuqU7g.vDZ7YV','service_start_date':'2025-03-14','market_code':'USA'}";
 *
 * // Example output:
 * // {
 * //   device_id: "YuA6.lAAGZuqU7g.vDZ7YV",
 * //   service_start_date: "2025-03-14",
 * //   market_code: "USA"
 * // }
 */

const parseInputValue = (value: string): RATCustomObject => {
  try {
    const cleanValue = value.replace(/^\{|\}\\$/g, '');
    const pairs = (cleanValue.match(/'[^']+':'[^']+'|'[^']+':\d+/g) || []) as string[];
    return pairs.reduce((acc, pair) => {
      const [originalKey, originalValue] = pair.split(':');
      const key = originalKey.replace(/'/g, '');
      const value = originalValue.replace(/'/g, '');

      const finalValue = /^\d+\\$/.test(value) ? Number(value) : value;

      return { ...acc, [key]: finalValue };
    }, {});
  } catch (error) {
    console.error('Error parsing input value:', error);
    return {};
  }
};

export const useRatQueryDataLayer = (deps: DependencyList = []): DataLayerValues => {
  const [values, setValues] = useState<DataLayerValues>({
    cpParams: {},
    cvParams: {},
  });

  useEffect(() => {
    // want to bind after the dom is fully rendered
    const timeoutId = setTimeout(() => {
      const cpInput = document.querySelector('#ratCustomParameters') as HTMLInputElement;
      const cvInput = document.querySelector('#ratCvEvent') as HTMLInputElement;

      if (cpInput) {
        const customParamsValue = parseInputValue(cpInput.value);
        setValues((prev) => ({
          ...prev,
          cpParams: customParamsValue,
        }));
      }

      if (cvInput) {
        const cvParamsValue = parseInputValue(cvInput.value);
        setValues((prev) => ({
          ...prev,
          cvParams: cvParamsValue,
        }));
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, deps);

  return values;
};

export const useRatBindComponentByCompId = () => {
  return useCallback((compid: string, eventType: string = 'appear') => {
    const selector = CSS.escape(compid);
    if (window.RAT && compid) {
      window.RAT.bind(window.RAT.$Selector(`[data-ratid=${selector}]`)?.off(eventType));
    }
  }, []);
};

/*
  SendCustomEventParams:
  - cpData   : Optional custom data layer object. If not provided, the hook uses the current cpParams.
  - pData    : Required payload object. For example: { compid: "xxx", additionalParam: "yyy" }
  - eventType: The type of the event, default to "click".
*/
export type SendCustomEventParams = {
  cpData?: RATCustomObject;
  pData: { [key: string]: string | number | string[] };
  eventType?: 'click' | 'appear' | 'pv' | 'async';
};

export const useRatSendCustomEvent = () => {
  const { cpParams } = useRatQueryDataLayer();

  return useCallback(
    (params: SendCustomEventParams) => {
      const cpData = params.cpData ?? cpParams;
      const eventType = params.eventType ?? 'click';

      const customEvent = {
        includeInput: true,
        options: ['url', 'ua'],
        eventType,
        cpData: cpData,
        pData: params.pData,
      };

      if (window.RAT && cpData) {
        window.RAT.addCustomEvent(customEvent);
      }
    },
    [cpParams],
  );
};

export const useRatSendCustomEventWithRef = () => {
  const { cpParams } = useRatQueryDataLayer();
  const cpParamsRef = useRef(cpParams);

  useEffect(() => {
    cpParamsRef.current = cpParams;
  }, [cpParams]);

  return useCallback((params: SendCustomEventParams) => {
    const cpData = params.cpData ?? cpParamsRef.current;
    const eventType = params.eventType ?? 'click';

    const customEvent = {
      includeInput: true,
      options: ['url', 'ua'],
      eventType,
      cpData: cpData,
      pData: params.pData,
    };

    if (window.RAT && cpData) {
      window.RAT.addCustomEvent(customEvent);
    }
  }, []);
};

export const useRatSendDialogEvents = ({ baseCompId }: UseRatDialogEventsParams) => {
  const { cpParams } = useRatQueryDataLayer();

  const sendRatEvent = useCallback(
    (eventType: DialogRatEventType, compidSuffix: string = '') => {
      const customEvent = {
        includeInput: true,
        options: ['url', 'ua'],
        eventType,
        cpData: cpParams,
        pData: {
          compid: [compidSuffix ? `${baseCompId}${compidSuffix}` : baseCompId],
        },
      };

      window.RAT && cpParams && window.RAT.addCustomEvent(customEvent);
    },
    [baseCompId, cpParams],
  );

  return {
    handleDialogOpen: useCallback(() => {
      sendRatEvent('click');
      sendRatEvent('appear', '_pup');
    }, [sendRatEvent]),

    handleDialogClose: useCallback(() => {
      sendRatEvent('click', '_pup_cls');
    }, [sendRatEvent]),
  };
};
